import React, { useEffect, useState } from 'react';
import {
    CardContent,
    Button,
    Grid,
    Box,
    Typography,
    Divider,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
    GridItem,
    SelectFilter,
    TextFieldFilter,
} from 'src/components/Search/components';
import { EnchancedModal } from 'src/components/Modals';
import useStore from 'src/hooks/useStore';
import {
    TDateRangeField,
    IFiltersMaintenancePackages,
    TSelectField,
    TInputField,
    IAdvancedMaintenancePackagesFilters,
} from './types';
import {
    TRangeValue,
    TSelectItem,
} from 'src/components/Search/components/types';
import { mapStores } from 'src/components/Search/utils';
import { MultiSelectFilter } from 'src/components/Search/components';
import { DateFilter } from 'src/components/Search/components';
import { MAINTENANCE_PACKAGE_DATE_RANGE_FIELD } from '../constants';

/**
 * Represents the advanced filters component for the maintenance packages list.
 *
 * @param {{
 *     open: boolean;
 *     setOpen: (open: boolean) => void;
 *     onApply: (filters: any) => void;
 *     values: IFiltersMaintenancePackages;
 * }} {
 *     open,
 *     setOpen,
 *     onApply,
 *     values,
 * }
 * @return {*}
 */
export const AdvancedFilters = ({
    open,
    setOpen,
    onApply,
    values,
}: IAdvancedMaintenancePackagesFilters) => {
    const { t } = useTranslation();
    const { stores, getStores } = useStore();
    const [currentStores, setCurrentStores] = useState<TSelectItem[]>([]);
    const [currentDateRange, setCurrentDateRange] =
        useState<TDateRangeField>('createdAt');

    const defaultFilters: IFiltersMaintenancePackages = {
        name: [],
        stores: [],
        isActive: '',
        'details.title': [],
        createdAt: null,
        updatedAt: null,
        isTabsEnabled: false,
        isSearching: false,
        tabValue: '',
        isQueryEnabled: false,
        query: '',
    };

    const [filters, setFilters] =
        useState<IFiltersMaintenancePackages>(defaultFilters);

    useEffect(() => {
        getStores();
    }, []);

    useEffect(() => {
        if (values && open) setFilters(values);
    }, [values, open]);

    const handleSelectChange =
        (field: TSelectField) => (selected: TSelectItem[]) => {
            setFilters({ ...filters, [field]: selected || [] });
        };

    const handleDateRangeChange =
        (key: TDateRangeField) => (range: TRangeValue) => {
            setFilters((prev) => ({ ...prev, [key]: range }));
        };

    const handleApply = () => {
        setOpen(false);
        onApply({
            ...filters,
            isSearching: true,
            isTabsEnabled: false,
            page: 0,
        });
    };

    const handleClear = () => {
        setOpen(false);
        onApply({
            ...defaultFilters,
            isSearching: false,
            isTabsEnabled: false,
        });
    };

    const handleClearDateRange = (key: TDateRangeField) => () =>
        setFilters((prev) => ({ ...prev, [key]: null }));

    const handleInputChange =
        (field: TInputField) => (e: React.ChangeEvent<HTMLInputElement>) => {
            setFilters({ ...filters, [field]: e.target.value });
        };

    useEffect(() => {
        setCurrentStores(mapStores(stores));
    }, [stores]);

    return (
        <EnchancedModal
            open={open}
            setOpen={setOpen}
            title={t('Buttons.AdvancedSearch')}
            maxWidth="md"
        >
            <CardContent
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 15,
                }}
            >
                <Typography>{t('ModalFilters.filters')}</Typography>

                <Grid container spacing={2}>
                    <GridItem>
                        <TextFieldFilter
                            label={t('ModalFilters.placeholder.name')}
                            value={filters.name}
                            onChange={handleInputChange('name')}
                            placeholder={t('ModalFilters.placeholder.name')}
                        />
                    </GridItem>
                    <GridItem>
                        <TextFieldFilter
                            label={t('ModalFilters.placeholder.detailsTitle')}
                            value={filters['details.title']}
                            onChange={handleInputChange('details.title')}
                            placeholder={t(
                                'ModalFilters.placeholder.detailsTitle'
                            )}
                        />
                    </GridItem>
                    <GridItem>
                        <TextFieldFilter
                            label={t(
                                'ModalFilters.placeholder.detailsDescription'
                            )}
                            value={filters['details.description']}
                            onChange={handleInputChange('details.description')}
                            placeholder={t(
                                'ModalFilters.placeholder.detailsDescription'
                            )}
                        />
                    </GridItem>
                    <GridItem>
                        <MultiSelectFilter
                            label={t('ModalFilters.store')}
                            options={currentStores}
                            value={filters['stores']}
                            onChange={handleSelectChange('stores')}
                            placeholder={t('ModalFilters.placeholder.store')}
                        />
                    </GridItem>
                    <GridItem>
                        <SelectFilter
                            label={t('ModalFilters.isActive')}
                            value={filters.isActive}
                            onChange={handleInputChange('isActive')}
                            options={[
                                {
                                    label: t('AdvancedAll.options'),
                                    value: '',
                                },
                                {
                                    label: t('maintenancePackage.Active'),
                                    value: 'true',
                                },
                                {
                                    label: t('maintenancePackage.Inactive'),
                                    value: 'false',
                                },
                            ]}
                        />
                    </GridItem>
                </Grid>
                <Divider />
                <Typography>{t('ModalFilters.dateRanges')}</Typography>

                <Grid container spacing={2}>
                    {MAINTENANCE_PACKAGE_DATE_RANGE_FIELD.map((field) => (
                        <GridItem key={field}>
                            <DateFilter
                                field={field}
                                value={filters[field]}
                                setCurrentDateRange={setCurrentDateRange}
                                onClear={handleClearDateRange(field)}
                                isActive={currentDateRange === field}
                                handleDateRangeChange={handleDateRangeChange}
                            />
                        </GridItem>
                    ))}
                </Grid>

                <Box
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '1em',
                    }}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleClear}
                        style={{
                            minHeight: 55,
                            minWidth: 250,
                        }}
                    >
                        {t('ModalFilters.clear')}
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        style={{
                            minHeight: 55,
                            minWidth: 250,
                        }}
                    >
                        {t('ModalFilters.apply')}
                    </Button>
                </Box>
            </CardContent>
        </EnchancedModal>
    );
};
